import { Rule } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import useDebounce from '../../../hooks/useDebounce';
import { isExecutiveManager } from '../../../lib/userUtils';
import { createQueryString } from '../../../lib/utils';
import {
  useJenkinsJobs,
  useProjectUsers,
  useTestCycleConfig,
  useTestCycles,
} from '../../../query/apiQueries';
import CustomIconButton from '../../elements/CustomIconButton';
import FilterButton from '../../elements/FilterButton';
import SearchBar from '../../elements/SearchBar';
import TestCycleTable from '../../elements/TableComponent';
import { tableHeaderTestCycles } from '../test-case/testCaseTableHead';
import AppliedFilters from '../test-cases/filters/AppliedFilters';
import AddTestCycleModal from './AddTestCycleModal';
import MarkTestCycleComplete from './MarkTestCycleComplete';
import TestCycleTableActions from './TestCycleTableActions';
import TestCycleFilterModal from './filters/TestCycleFilterModal';
import { defaultFilters, formatData } from './static-data';

const TestCyclesTabContentComponent = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const [showModal, setShowModal] = useState(false);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState(location.state?.filters || defaultFilters);
  const [searchValue, setSearchValue] = useState(location.state?.searchValue || '');
  const [page, setPage] = useState(location.state?.page || 1);
  const debouncedSearchQuery = useDebounce(searchValue, 500);

  const [perPage, setPerPage] = useState(10);
  const queryString = createQueryString(filters);

  const { data: config = [], isLoading: loadingConfig } = useTestCycleConfig(projectId);
  const { data: projectUsers = [], isLoading: loadingUsers } = useProjectUsers(projectId);
  const { data: jobs = [], isLoading: loadingJobs, error: jobError } = useJenkinsJobs(projectId);

  const {
    data: testCycleResponse,
    isLoading: loadingCycles,
    isFetching: isFetchingCycles,
    refetch,
    error: testCycleError,
  } = useTestCycles(
    {
      page,
      perPage,
      search: debouncedSearchQuery,
      queryString,
      projectId,
    }
  );

  const testcycles = testCycleResponse?.testcycles || [];
  const totalCount = testCycleResponse?.total || 0;
  const isInitialLoading = loadingCycles || loadingUsers || loadingJobs || loadingConfig;

  useEffect(() => {
    if (testCycleError) {
      addNotification('danger', testCycleError.message || 'Failed to fetch test cycles. Please try again later.');
    }
  }, [testCycleError]);

  const handleNavigationStateUpdate = useCallback(() => {
    navigate('.', {replace: true,state: { filters, searchValue, page } });
  }, [filters, searchValue, page, navigate]);

  useEffect(() => {
    handleNavigationStateUpdate();
  }, [debouncedSearchQuery, filters, page, handleNavigationStateUpdate, perPage]);

  const formattedData = useMemo(() => formatData(testcycles), [testcycles]);

  const ActionButton = useCallback(
    (row) => (
      <>
      {!isExecutiveManager() &&
        <MarkTestCycleComplete
          row={row}
          addNotification={addNotification}
          fetchTestCycles={refetch}
        />
        }
        <CustomIconButton
          title='Go to Test Executions'
          onClick={() => navigate(`/project/${projectId}/testcycles/${row.key}`)}>
           <Rule sx={{ color: '#1b3f87' }} />
        </CustomIconButton>
      </>
    ),
    [refetch, projectId]
  );
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setPage(1);
  };

  const handleRefresh = () => {
    refetch();
    setSelected([]);
    setSelectedRows([]);
  }

  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
    navigate('.', {
        replace: true,
        state: { filters, searchValue, page: 1 },
    });
  };
  return (
    <Container fluid className="mt-2">
      <Row>
        <Col>
          <Typography variant="h4" className="mb-3">Test Cycles</Typography>
        </Col>
      </Row>
      <Row>
        <Col className="d-flex" md="4">
          <SearchBar
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            clearSearch={() => handleSearch('')}
            placeholder="Search test cycles"
          />
          <FilterButton onClick={() => setShowFilterModal(true)} />
        </Col>
        {!isExecutiveManager() && (
          <Col className="d-flex justify-content-end">
          <TestCycleTableActions
            handleOpenTestCycleModal={() => setShowModal(true)}
            isSelectedItems={selectedRows?.length}
            selectedItems={selected}
            refreshData={handleRefresh}
            selectedRows={selectedRows}
            addNotification={addNotification}
          />
        </Col>
      )}
      </Row>
      <Row className="mt-3">
        <Col>
          <AppliedFilters
            filters={filters}
            statuses={config?.status}
            purposeOptions={config?.purpose}
            typeOptions={config?.type}
            setFilters={setFilters}
            projectUsers={projectUsers}
            tab="cycles"
          />
        </Col>
      </Row>
      <TestCycleTable
        data={formattedData || []}
        headerTabs={tableHeaderTestCycles}
        showCheckbox={isExecutiveManager() ? false : true}
        page={page}
        setPerPage={handlePageSizeChange}
        perPage={perPage}
        order={order}
        setOrder={setOrder}
        isLoading={isInitialLoading || isFetchingCycles}
        defaultSort="created_at"
        selected={selected}
        setSelected={setSelected}
        setSelectedRows={setSelectedRows}
        totalCount={totalCount}
        showPages
        striped
        handlePageChange={handlePageChange}
        action={ActionButton}
        tablekey="test cycles"
        showCount
        showRowCount={true}
      />
      <AddTestCycleModal
        showModal={showModal}
        handleClose={() => setShowModal(false)}
        fetchTestCycles={handleRefresh}
        addNotification={addNotification}
        statuses={config?.status}
        purposeOptions={config?.purpose}
        typeOptions={config?.type}
        jobs={jobs}
        jobError={jobError}
        projectUsers={projectUsers}
      />
      <TestCycleFilterModal
        showModal={showFilterModal}
        handleClose={() => setShowFilterModal(false)}
        projectUsers={projectUsers}
        statuses={config?.status}
        purposeOptions={config?.purpose}
        typeOptions={config?.type}
        isLoading={isInitialLoading}
        addNotification={addNotification}
        setFilters={setFilters}
        filters={filters}
        setPage={setPage}
      />
    </Container>
  );
};

export default TestCyclesTabContentComponent;
